/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  useColorModeValue,
  IconButton,
  Textarea,
  Input,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import { useHistoryList } from './hooks/useHistoryList';
import useDebounce from 'constants/helper/useDebounce';
import { useGetHistoryById } from './hooks/useGetHistoryById';
import { getUser } from 'constants/helper/authStorage';
import { useAddRemark } from './hooks/useAddRemark';
import { useQueryClient } from '@tanstack/react-query';
import { usePatientAllListByObjId } from '../patientsHistory/hooks/usePatientAllListByObjId';
import formatDateTime from 'constants/helper/formatDateTime';
import ModernStepper from './components/ModernStepper';
import TreatmentDetailsCard from './components/TreatmentDetailsCard';
import { AnimatePresence } from 'framer-motion';

function PatientsHistoryManagement() {
  const queryClient = useQueryClient();
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dateFilterValues, setDateFilterValues] = useState({
    year: '',
    month: '',
    fromDate: '',
    toDate: '',
  });

  const [page, setPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const STEPS_PER_PAGE = 11;

  const [stepPage, setStepPage] = useState(0);
  const [isRemarkOpen, setIsRemarkOpen] = useState(false);
  const [remarkPatient, setRemarkPatient] = useState(null);

  const [remarkData, setRemarkData] = useState({
    score: '',
    reliefPercent: {}, // Changed to object for multi-disease support
    remark: '',
  });

  const handleRemarkChange = (fieldOrId, value) => {
    // Check if the fieldOrId is a disease ID from the currently selected remark patient
    const isDiseaseId = remarkPatient?.diseases?.some((d) => d._id === fieldOrId);

    if (isDiseaseId) {
      setRemarkData((prev) => ({
        ...prev,
        reliefPercent: {
          ...prev.reliefPercent,
          [fieldOrId]: value,
        },
      }));
    } else {
      setRemarkData((prev) => ({ ...prev, [fieldOrId]: value }));
    }
  };

  const { patientHistory, totalPages, isLoading } = useHistoryList(
    page,
    debouncedSearch,
    undefined,
    dateFilterValues,
  );

  const {
    patientHistory: patientDetails = [],
    totalPages: historyTotalPages,
    isLoading: isLoadingPatientHistory,
  } = usePatientAllListByObjId(historyPage, selectedPatient);

  const [activeVisitIndex, setActiveVisitIndex] = useState(0);
  const visits = patientDetails || [];

  const activeVisit = visits[activeVisitIndex];
  const totalStepPages = Math.ceil(visits.length / STEPS_PER_PAGE);

  const visibleVisits = visits.slice(
    stepPage * STEPS_PER_PAGE,
    stepPage * STEPS_PER_PAGE + STEPS_PER_PAGE,
  );

  const handleSearch = (value) => {
    setSearchString(value);
  };

  const handleDateFilterChange = (values) => {
    setDateFilterValues(values);
    setPage(1);
  };

  const tableData = patientHistory?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
  }));

  const userDetails = getUser();

  const formattedData = tableData?.map((item) => {
    const doctorName = userDetails?.name;
    const diseases = item.diseases || [];

    const engList = diseases.map((d) => d.diseaseEnglish).filter(Boolean);
    const hindList = diseases.map((d) => d.diseaseHindi).filter(Boolean);

    const bottle1Values = [
      item?.bottle1?.E,
      item?.bottle1?.R1,
      item?.bottle1?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');

    const bottle2Values = [
      item?.bottle2?.E,
      item?.bottle2?.R1,
      item?.bottle2?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');
    const bottle3Values = [
      item?.bottle3?.E,
      item?.bottle3?.R1,
      item?.bottle3?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');
    const bottle4Values = [
      item?.bottle4?.E,
      item?.bottle4?.R1,
      item?.bottle4?.R2,
    ].filter((v) => v !== undefined && v !== null && v !== '');

    return {
      ...item,
      patientId: item?.patientObjId?.patientId,
      doctorName: doctorName,
      patientName: item?.patientObjId?.name,
      patientMobile: item?.patientObjId?.mobile,
      date: item?.createdAt?.split('T')[0],

      bottle1:
        bottle1Values.length > 1
          ? bottle1Values.join(' + ')
          : bottle1Values[0] || '- - -',
      bottle2:
        bottle2Values.length > 1
          ? bottle2Values.join(' + ')
          : bottle2Values[0] || '- - -',
      bottle3:
        bottle3Values.length > 1
          ? bottle3Values.join(' + ')
          : bottle3Values[0] || '- - -',
      bottle4:
        bottle4Values.length > 1
          ? bottle4Values.join(' + ')
          : bottle4Values[0] || '- - -',

      diseaseEng: engList.length > 1 ? engList.join(' / ') : engList[0] || '',

      diseaseHind:
        hindList.length > 1 ? hindList.join(' / ') : hindList[0] || '',
    };
  });

  const handleView = (row) => {
    setSelectedPatient(row?.patientObjId?._id);
    setHistoryPage(1);
    setActiveVisitIndex(0);
    setStepPage(0);
    setIsViewOpen(true);
  };

  const handleClose = () => {
    setIsViewOpen(false);
    setSelectedPatient(null);
  };

  const modalBg = useColorModeValue('white', 'gray.800');
  const modalBorder = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('blue.600', 'blue.700');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const textMuted = useColorModeValue('gray.600', 'gray.400');
  const textStrong = useColorModeValue('gray.700', 'gray.200');
  const spinnerEmpty = useColorModeValue('gray.200', 'gray.600');

  const isReliefValid = React.useMemo(() => {
    const diseases = remarkPatient?.diseases || [];
    if (diseases.length === 0) return false;

    return diseases.every((d) => {
      const val = remarkData.reliefPercent[d._id];
      return (
        val !== undefined &&
        val !== '' &&
        Number(val) >= 10 &&
        Number(val) <= 100
      );
    });
  }, [remarkData.reliefPercent, remarkPatient?.diseases]);

  const isFormValid = isReliefValid;

  const { mutate: addRemarkMutation, isLoading: isAddingRemark } = useAddRemark(
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['patientHistoryListForAdmin']);

        setIsRemarkOpen(false);
        setRemarkData({
          score: '',
          reliefPercent: '',
          remark: '',
        });
      },
    },
  );

  return (
    <Box p={4} mt={10}>
      {/* TABLE VIEW */}
      <ReusableTable
        data={formattedData}
        columns={[
          { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },

          {
            key: 'doctorName',
            label: 'Doctor Name',
            width: '300px',
            headerSize: '14px',
          },
          {
            key: 'patientId',
            label: 'Patient ID',
            width: '300px',
            headerSize: '14px',
          },
          {
            key: 'patientName',
            label: 'Patient Name',
            width: '350px',
            headerSize: '14px',
          },
          {
            key: 'patientMobile',
            label: 'Mobile',
            width: '300px',
            headerSize: '14px',
          },
          {
            key: 'date',
            label: 'Date',
            width: '300px',
            headerSize: '14px',
          },
          {
            key: 'diseaseEng',
            label: 'Disease Name Eng',
            width: '350px',
            headerSize: '14px',
          },
          {
            key: 'diseaseHind',
            label: 'Disease Name Hin',
            width: '350px',
            headerSize: '14px',
          },
          {
            key: 'action',
            label: 'Action',
            width: '200px',
            render: (_, row) => (
              <Flex gap={2}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => handleView(row)}
                >
                  History
                </Button>
                {row?.isRemarkSubmitted === false && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    onClick={() => {
                      setRemarkPatient(row);
                      setRemarkData({
                        score: '',
                        reliefPercent: '',
                        remark: '',
                      });
                      setIsRemarkOpen(true);
                    }}
                  >
                    Remark
                  </Button>
                )}
              </Flex>
            ),
          },
        ]}
        page={page}
        totalPages={totalPages || 1}
        loading={isLoading}
        // onRowClick={() => {}}
        onPageChange={(newPage) => setPage(newPage)}
        showTopAction={true}
        showAddBtn={false}
        topAction={{
          title: 'Patient History',
          label: 'Add Protocol',
          icon: <AddIcon />,
          // onClick: () => setShowForm(true),
        }}
        showViewToggle={false}
        showSearch={true}
        onSearch={handleSearch}
        showDateFilter={true}
        dateFilterValues={dateFilterValues}
        onDateFilterChange={handleDateFilterChange}
      />

      <Modal
        isOpen={isViewOpen}
        onClose={handleClose}
        size={{ base: 'full', md: 'xl', xl: '6xl' }}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.700" />

        <ModalContent
          borderRadius="xl"
          overflow="hidden"
          bg={modalBg}
          borderWidth="1px"
          borderColor={modalBorder}
        >
          <ModalHeader
            bg={headerBg}
            color="white"
            fontSize="lg"
            fontWeight="bold"
          >
            Patient Treatment Details
          </ModalHeader>

          <ModalCloseButton color="white" />

          <ModalBody p={6} maxH="80vh" overflowY="auto">
            {/* ================= MODERN VISITS UI ================= */}
            <Box>
              {visits.length > 0 && (
                <Box mb={8}>
                  <ModernStepper
                    steps={visits.map((v, i) => ({
                      label: v.visitDate || `Visit`,
                    }))}
                    activeStep={activeVisitIndex}
                    setActiveStep={setActiveVisitIndex}
                    stepPage={stepPage}
                    setStepPage={setStepPage}
                    stepsPerPage={STEPS_PER_PAGE}
                    startIndex={(historyPage - 1) * 10} // Fix numbering
                    hasNextPage={historyPage < (historyTotalPages || 1)}
                    hasPrevPage={historyPage > 1}
                    onNextPage={() => {
                      setHistoryPage((p) => p + 1);
                      setStepPage(0);
                      setActiveVisitIndex(0);
                    }}
                    onPrevPage={() => {
                      setHistoryPage((p) => p - 1);
                      setStepPage(0);
                      setActiveVisitIndex(0);
                    }}
                  />
                </Box>
              )}

              {isLoadingPatientHistory ? (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  h="400px"
                >
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor={spinnerEmpty}
                    color="blue.500"
                    size="xl"
                  />
                  <Text mt={4} fontWeight="semibold" color={textStrong}>
                    Loading Records...
                  </Text>
                </Flex>
              ) : (
                <AnimatePresence mode="wait">
                  {activeVisit && (
                    <TreatmentDetailsCard
                      key={activeVisit._id}
                      visit={activeVisit}
                    />
                  )}
                </AnimatePresence>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ================= REMARK MODAL ================= */}
      <Modal
        isOpen={isRemarkOpen}
        onClose={() => setIsRemarkOpen(false)}
        size={{ base: 'full', md: 'md' }}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" />

        <ModalContent borderRadius="xl" bg={modalBg}>
              <ModalHeader bg={headerBg} color="white">
            Add Patient Relief
          </ModalHeader>

          <ModalCloseButton color="white" />

          <ModalBody p={6}>
            <Flex direction="column" gap={4}>
              {/* SCORE (Commented out to match ProtocolPage) */}
              {/* <Box>
                <Flex justify="space-between" mb={1}>
                  <Text fontSize="xs" color={textMuted}>
                    Score Remedy
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color="orange.500">
                    {remarkData.score || 0}/5
                  </Text>
                </Flex>

                <Slider
                  value={Number(remarkData.score) || 0}
                  min={0}
                  max={5}
                  step={1}
                  onChange={(val) => handleRemarkChange('score', val)}
                  colorScheme={
                    remarkData.score <= 2
                      ? 'red'
                      : remarkData.score <= 4
                        ? 'yellow'
                        : 'green'
                  }
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>

                  <SliderThumb boxSize={4}>
                    <Box color="orange.500" />
                  </SliderThumb>
                </Slider>

                <Flex justify="space-between" mt={1}>
                  <Text fontSize="10px" color={textMuted}>
                    Poor
                  </Text>
                  <Text fontSize="10px" color={textMuted}>
                    Excellent
                  </Text>
                </Flex>
                {!isScoreValid && (
                  <Text fontSize="10px" color="red.500" mt={1}>
                    Score is required
                  </Text>
                )}
              </Box> */}

              {/* RELIEF */}
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="blue.500"
                  mb={3}
                  textTransform="uppercase"
                >
                  Clinical Improvement by Disease
                </Text>
                <VStack align="stretch" spacing={4}>
                  {remarkPatient?.diseases?.map((disease) => {
                    const reliefValue =
                      remarkData?.reliefPercent?.[disease._id] || '';

                    return (
                      <Box
                        key={disease._id}
                        p={3}
                        bg={cardBg}
                        rounded="lg"
                        border="1px solid"
                        borderColor={cardBorder}
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="bold" fontSize="sm">
                            {disease.diseaseEnglish}
                          </Text>
                          <Text fontSize="xs" color={textMuted}>
                            Relief (%)
                          </Text>
                        </Flex>

                        <Input
                          type="number"
                          size="sm"
                          placeholder="10 - 100"
                          value={reliefValue}
                          bg={modalBg}
                          onChange={(e) =>
                            handleRemarkChange(disease._id, e.target.value)
                          }
                        />

                        {reliefValue &&
                          (Number(reliefValue) < 10 ||
                            Number(reliefValue) > 100) && (
                            <Text fontSize="10px" color="red.500" mt={1}>
                              Enter relief between 10 and 100
                            </Text>
                          )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>

              {/* REMARK (Commented out to match ProtocolPage) */}
              {/* <Box>
                <Text fontSize="xs" mb={1} color={textMuted}>
                  Remark
                </Text>
                <Textarea
                  size="sm"
                  resize="none"
                  rows={3}
                  placeholder="Treatment works well…"
                  value={remarkData.remark}
                  onChange={(e) => handleRemarkChange('remark', e.target.value)}
                />
                {!isRemarkValid && (
                  <Text fontSize="10px" color="red.500" mt={1}>
                    Remark is required
                  </Text>
                )}
              </Box> */}

              {/* SUBMIT */}
              <Flex justify="flex-end" gap={3} mt={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsRemarkOpen(false)}
                >
                  Cancel
                </Button>

                <Button
                  size="sm"
                  colorScheme="green"
                  isDisabled={!isFormValid}
                  isLoading={isAddingRemark}
                  onClick={() => {
                    const diseaseReliefs = remarkPatient?.diseases?.map((d) => ({
                      diseaseId: d._id,
                      reliefPercent: Number(remarkData.reliefPercent[d._id]),
                    }));

                    const payload = {
                      patientObjId: remarkPatient?.patientObjId?._id,
                      givenTreatmentProtocolId: remarkPatient?._id,
                      diseaseReliefs,
                      score: Number(remarkData.score) || 0,
                      remark: remarkData.remark.trim() || '',
                    };

                    addRemarkMutation(payload);
                  }}
                >
                  Submit
                </Button>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default PatientsHistoryManagement;
